import React, { useState } from 'react';
import { Button } from '../ui/Button';
import api from '../../lib/api';
import { Contact, ContactStatus, ContactGroup } from '../../types';
import { useOrgFeatures } from '../../hooks/useOrgFeatures';
import {
  X, ChevronDown, ChevronUp, Search, Plus, Camera, Paperclip, Trash2,
} from 'lucide-react';
import HebrewDateInput from '../common/HebrewDateInput';
import AddCustomFieldInline from './AddCustomFieldInline';
import EmailListEditor, { EmailRow, rowsToPayload } from './EmailListEditor';

// ─── Add Contact Form ─────────────────────────────────────────────────────────
// Shared between the Contacts page ("איש קשר חדש") and the Tuition screen
// ("+ תלמיד"). When type==='student' a tuition-enrollment block appears
// (institution / class / family / paying-parents) that creates the full
// Contact → Family → Payers → Student chain. Launched from tuition with a
// locked institutionContext so the מוסד is fixed.

const FIELD_CLASS = 'w-full px-4 py-2 border border-gray-300 rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm';

interface CustomFieldDefinition {
  id: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'select' | 'checkbox';
  options?: string[];
  key?: string;
  groupId?: string;
  order?: number;
  required?: boolean;
  helpText?: string;
  placeholder?: string;
  archivedAt?: string | null;
  visibility?: string[];
  /** '' = all contacts, 'supported' = beneficiaries, 'student' = students */
  appliesToType?: string;
}

interface CustomFieldGroup {
  id: string;
  label: string;
  order?: number;
  collapsedByDefault?: boolean;
}

const DEFAULT_GROUP: CustomFieldGroup = { id: 'default', label: 'שדות מותאמים', order: 0 };

function isFieldVisibleToRole(field: CustomFieldDefinition, role: string | undefined): boolean {
  if (!field.visibility || field.visibility.length === 0) return true;
  if (!role) return false;
  return field.visibility.includes(role);
}

function isFieldArchived(field: CustomFieldDefinition): boolean {
  return Boolean(field.archivedAt);
}

// Local copy — ContactsPage has its own getDisplayName used across the page;
// the form only needs it for the duplicate-warning rows.
function getDisplayName(contact: Contact): string {
  if ((contact as any).firstName || (contact as any).lastName) {
    return [(contact as any).firstName, (contact as any).lastName].filter(Boolean).join(' ');
  }
  return (contact as any).name || '';
}

interface PendingDoc { name: string; url: string; fileType?: string; size?: number; }
interface Payer { name: string; phone: string; email?: string; role: string; percent: string; }

export interface AddContactFormProps {
  onClose: () => void;
  /** Notified after a successful save. Carries the new contact id and, when a
   *  tuition enrollment was created, the family id (the tuition screen uses it
   *  to select the new family). */
  onSaved: (created?: { contactId?: string; familyId?: string }) => void;
  /** Preselect the contact type (e.g. 'student' when launched from tuition). */
  initialType?: string;
  /** When launched from the tuition screen the institution is fixed/known. */
  institutionContext?: {
    institutionId: string;
    instName?: string;
    presetUnitId?: string;
    lock?: boolean;
  };
}

const AddContactForm: React.FC<AddContactFormProps> = ({ onClose, onSaved, initialType, institutionContext }) => {
  // Org feature flags — gates the training-groups types (מתאמן/מאמן) in the
  // type picker. A form opened with initialType='trainee'/'coach' always shows
  // its label even before the flags load (see the OR in the render).
  const { features } = useOrgFeatures();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    nationalId: '',
    city: '',
    type: (initialType || 'member') as string,
    status: 'active' as ContactStatus,
    tags: '',
    source: '',
    photo: '',
    birthDate: '',
    gender: '',
    phoneHome: '',
    whatsapp: '',
    street: '',
    streetNumber: '',
    zipCode: '',
    poBox: '',
    country: 'ישראל',
    synagogue: '',
    rabbi: '',
    ethnicity: '',
    maritalStatus: '',
    spouseName: '',
    numberOfChildren: '',
    communicationPref: '',
    referredBy: '',
    studentParents: { fatherName: '', fatherPhone: '', fatherEmail: '', fatherId: '', motherName: '', motherPhone: '', motherEmail: '', motherId: '' },
  });
  const setSP = (field: string, value: string) =>
    setFormData(f => ({ ...f, studentParents: { ...(f as any).studentParents, [field]: value } }));
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, string>>({});
  const [customFields, setCustomFields] = useState<CustomFieldDefinition[]>([]);
  const [fieldGroups, setFieldGroups] = useState<CustomFieldGroup[]>([]);
  const [saving, setSaving] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [duplicates, setDuplicates] = useState<Contact[]>([]);
  const [selectedDuplicate, setSelectedDuplicate] = useState<string | null>(null);
  const [showAddFieldForm, setShowAddFieldForm] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // ריבוי מיילים: הכוכב = הדיפולט (נשמר ל-email); השאר ל-additionalEmails.
  // formData.email מסונכרן לדיפולט כדי שבדיקת הכפילויות תמשיך לעבוד.
  const [emailRows, setEmailRows] = useState<EmailRow[]>([{ address: '', isDefault: true }]);
  const handleEmailRows = (rows: EmailRow[]) => {
    setEmailRows(rows);
    const def = rows.find(r => r.isDefault)?.address || '';
    setFormData(f => ({ ...f, email: def }));
  };
  // Assign the new contact to existing groups on creation. Only static groups
  // accept manual members (dynamic groups are rule-based), so the picker only
  // lists those.
  const [groups, setGroups] = useState<ContactGroup[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [groupSearch, setGroupSearch] = useState('');
  const [showGroupPicker, setShowGroupPicker] = useState(false);
  const groupPickerRef = React.useRef<HTMLDivElement>(null);

  // ── Photo + documents ───────────────────────────────────────────────────────
  const photoInputRef = React.useRef<HTMLInputElement>(null);
  const docInputRef = React.useRef<HTMLInputElement>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [pendingDocs, setPendingDocs] = useState<PendingDoc[]>([]);

  // ── Tuition enrollment (only when type==='student') ───────────────────────────
  const [institutions, setInstitutions] = useState<any[]>([]);
  const [selectedInstitutionId, setSelectedInstitutionId] = useState<string>(institutionContext?.institutionId || '');
  const [units, setUnits] = useState<any[]>([]);
  const [unitId, setUnitId] = useState<string>(institutionContext?.presetUnitId || '');
  // Billing family name defaults to the student's last name; only exposed as a
  // field when the payer family name differs (e.g. divorced/blended families).
  const [customFamilyName, setCustomFamilyName] = useState('');
  const [showFamilyNameField, setShowFamilyNameField] = useState(false);
  // Payment split between the parents (divorced) — default: first parent 100%.
  const [splitPayment, setSplitPayment] = useState(false);
  const [fatherPercent, setFatherPercent] = useState('50');
  const [motherPercent, setMotherPercent] = useState('50');
  const [showExtraGroups, setShowExtraGroups] = useState(false);

  const effectiveFamilyName = (showFamilyNameField && customFamilyName.trim())
    ? customFamilyName.trim()
    : (formData.lastName.trim() ? `משפחת ${formData.lastName.trim()}` : '');

  // Build the family payer rows from the single parents block. Without a split
  // the first filled parent carries 100%; the other is kept as a payer (0%) so
  // both parents stay linked to the family + the parents CRM groups.
  const buildPayers = (): Payer[] => {
    const sp = formData.studentParents;
    const out: Payer[] = [];
    const hasF = !!sp.fatherName.trim(), hasM = !!sp.motherName.trim();
    // a split only makes sense with two parents — one parent always pays 100%
    const split = splitPayment && hasF && hasM;
    if (hasF) out.push({
      name: sp.fatherName.trim(), phone: sp.fatherPhone, email: sp.fatherEmail, role: 'father',
      percent: split ? fatherPercent : '100',
    });
    if (hasM) out.push({
      name: sp.motherName.trim(), phone: sp.motherPhone, email: sp.motherEmail, role: 'mother',
      percent: split ? motherPercent : (hasF ? '0' : '100'),
    });
    return out;
  };

  const extractError = (err: any): string => {
    const data = err?.response?.data;
    if (typeof data === 'string') return data;
    if (data?.message) return String(data.message);
    if (data?.error) return String(data.error);
    if (err?.message) return String(err.message);
    return 'שגיאה לא ידועה. נסה שוב.';
  };

  const userRole = (() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || 'null');
      return u?.role as string | undefined;
    } catch { return undefined; }
  })();

  // Load custom fields + field groups on mount
  React.useEffect(() => {
    api.get('/permissions/contact-custom-fields')
      .then(res => {
        if (Array.isArray(res.data)) setCustomFields(res.data);
      })
      .catch(() => {});
    api.get('/permissions/contact-field-groups')
      .then(res => {
        if (Array.isArray(res.data)) setFieldGroups(res.data);
      })
      .catch(() => {});
    api.get('/groups')
      .then(res => {
        // Only static groups can take manually-assigned members.
        if (Array.isArray(res.data)) {
          setGroups(res.data.filter((g: ContactGroup) => g.kind !== 'dynamic'));
        }
      })
      .catch(() => {});
  }, []);

  // Load institutions lazily once the contact is a student (for the מוסד picker
  // + tier labels). Skipped for non-student contacts.
  React.useEffect(() => {
    if (formData.type !== 'student' || institutions.length) return;
    api.get('/tuition/institutions')
      .then(res => { if (Array.isArray(res.data)) setInstitutions(res.data); })
      .catch(() => {});
  }, [formData.type]);

  // Load the chosen institution's class/grade structure.
  React.useEffect(() => {
    if (!selectedInstitutionId) { setUnits([]); return; }
    api.get(`/tuition/institutions/${selectedInstitutionId}/structure`)
      .then(res => setUnits(Array.isArray(res.data) ? res.data : []))
      .catch(() => setUnits([]));
  }, [selectedInstitutionId]);

  // Close the group picker when clicking outside it.
  React.useEffect(() => {
    if (!showGroupPicker) return;
    const onClick = (e: MouseEvent) => {
      if (groupPickerRef.current && !groupPickerRef.current.contains(e.target as Node)) {
        setShowGroupPicker(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [showGroupPicker]);

  const toggleGroup = (id: string) =>
    setSelectedGroupIds(ids => ids.includes(id) ? ids.filter(g => g !== id) : [...ids, id]);

  // Visible non-archived fields, grouped + ordered.
  // Student-scoped fields (appliesToType==='student') only show when the new
  // contact is a student — mirrors ContactDetailPage's scope filtering.
  const visibleFieldsByGroup = React.useMemo(() => {
    const visible = customFields
      .filter((f) => !isFieldArchived(f) && isFieldVisibleToRole(f, userRole)
        && (f.appliesToType !== 'student' || formData.type === 'student'))
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const groupsById = new Map<string, CustomFieldGroup>();
    for (const g of fieldGroups) groupsById.set(g.id, g);
    if (!groupsById.has(DEFAULT_GROUP.id)) groupsById.set(DEFAULT_GROUP.id, DEFAULT_GROUP);
    const buckets = new Map<string, CustomFieldDefinition[]>();
    for (const f of visible) {
      const gid = f.groupId || DEFAULT_GROUP.id;
      const arr = buckets.get(gid) ?? [];
      arr.push(f);
      buckets.set(gid, arr);
    }
    const ordered: { group: CustomFieldGroup; fields: CustomFieldDefinition[] }[] = [];
    for (const [gid, fields] of buckets) {
      const group = groupsById.get(gid) || { id: gid, label: gid, order: 99 };
      ordered.push({ group, fields });
    }
    ordered.sort((a, b) => (a.group.order ?? 0) - (b.group.order ?? 0));
    return ordered;
  }, [customFields, fieldGroups, userRole, formData.type]);

  const set = (field: string, value: string) => setFormData(f => ({ ...f, [field]: value }));

  // ── Tuition: class options derived from the structure tree (GROUP leaves with
  // their level prefix, else LEVELs) — mirrors the old AddStudentModal. ─────────
  const tGroups = units.filter(u => u.tier === 'GROUP');
  const tLevels = units.filter(u => u.tier === 'LEVEL');
  const levelName = (lid: string) => tLevels.find(l => String(l._id) === String(lid))?.name || '';
  const classOptions = tGroups.length
    ? tGroups.map(g => ({ id: g._id, label: `${levelName(g.parentUnitId)} ${g.name}`.trim() }))
    : tLevels.map(l => ({ id: l._id, label: l.name }));
  const selectedInst = institutions.find(i => String(i._id) === String(selectedInstitutionId));
  const tierLabels = selectedInst?.tierLabels || { level: 'שכבה', group: 'כיתה' };
  const classLabel = tierLabels.group || tierLabels.level || 'כיתה';
  const lockedInstName = institutionContext?.instName || selectedInst?.name || 'מוסד';

  // ── Photo / document upload handlers ──────────────────────────────────────────
  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/upload/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setFormData(f => ({ ...f, photo: res.data?.url || '' }));
    } catch {
      alert('שגיאה בהעלאת התמונה');
    } finally {
      setUploadingPhoto(false);
      if (photoInputRef.current) photoInputRef.current.value = '';
    }
  };

  const handleDocChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/upload/document', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPendingDocs(d => [...d, { name: res.data?.originalName || file.name, url: res.data?.url, fileType: res.data?.fileType, size: res.data?.size }]);
    } catch {
      alert('שגיאה בהעלאת המסמך');
    } finally {
      setUploadingDoc(false);
      if (docInputRef.current) docInputRef.current.value = '';
    }
  };

  // Check for duplicates when phone or email changes
  const checkDuplicates = async () => {
    if (!formData.phone && !formData.email) {
      setDuplicates([]);
      return;
    }
    try {
      const params = new URLSearchParams();
      if (formData.phone) params.append('phone', formData.phone);
      if (formData.email) params.append('email', formData.email);
      const res = await api.get(`/contacts/check-duplicate?${params}`);
      setDuplicates(res.data || []);
    } catch (err) {
      console.error('Failed to check duplicates:', err);
    }
  };

  // Debounce duplicate check
  React.useEffect(() => {
    const timer = setTimeout(checkDuplicates, 500);
    return () => clearTimeout(timer);
  }, [formData.phone, formData.email]);

  // Build the base create payload shared by the normal + merge paths.
  const buildPayload = () => {
    const name = [formData.firstName, formData.lastName].filter(Boolean).join(' ') || formData.email || formData.phone;
    const payload: any = {
      ...formData,
      name,
      phone: formData.phone,
      tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
      customFields: customFieldValues,
    };
    // ריבוי מיילים: הדיפולט → email, השאר → additionalEmails
    const emails = rowsToPayload(emailRows);
    payload.email = emails.email;
    payload.additionalEmails = emails.additionalEmails;
    if (pendingDocs.length) payload.documents = pendingDocs;
    if (formData.numberOfChildren) payload.numberOfChildren = Number(formData.numberOfChildren);
    if (!formData.source) delete payload.source;
    if (!formData.birthDate) delete payload.birthDate;
    if (!formData.gender) delete payload.gender;
    if (!formData.maritalStatus) delete payload.maritalStatus;
    if (!formData.communicationPref) delete payload.communicationPref;
    if (selectedGroupIds.length) payload.groupIds = selectedGroupIds;
    return payload;
  };

  // After the Contact exists, create the tuition Family + Payers + Student.
  // Best-effort: a failure here leaves the contact in place and surfaces a
  // non-fatal warning. Returns the created familyId (or undefined).
  const enrollTuition = async (contactId: string): Promise<string | undefined> => {
    const famRes = await api.post('/tuition/families', { institutionId: selectedInstitutionId, displayName: effectiveFamilyName });
    const famId = famRes.data._id;
    for (const p of buildPayers()) {
      await api.post(`/tuition/families/${famId}/payers/contact`, {
        name: p.name, phone: p.phone, email: p.email || '', role: p.role, percent: Number(p.percent) || 0,
      });
    }
    await api.post('/tuition/students', {
      institutionId: selectedInstitutionId, familyId: famId, orgUnitId: unitId || undefined,
      contactId, firstName: formData.firstName, lastName: formData.lastName,
      birthDate: formData.birthDate || undefined, idNumber: formData.nationalId || undefined,
      phone: formData.phone, email: formData.email,
    });
    return famId;
  };

  const mergeToDuplicate = async (duplicateContact: Contact) => {
    // Create the current contact first, then merge into the duplicate (which becomes the master)
    setSaving(true);
    setSubmitError(null);
    try {
      const payload = buildPayload();

      // Create new contact
      const newContactRes = await api.post('/contacts', payload);
      const newContact = newContactRes.data;

      // Merge new contact into the duplicate (duplicate becomes master)
      await api.post('/contacts/merge', {
        masterId: duplicateContact._id,
        duplicateId: newContact._id,
      });

      // A merged student still needs the tuition enrollment (family + class) —
      // without this the student silently never appeared in the class.
      if (formData.type === 'student' && selectedInstitutionId && effectiveFamilyName) {
        try {
          const famId = await enrollTuition(duplicateContact._id);
          onSaved({ contactId: duplicateContact._id, familyId: famId });
          onClose();
          return;
        } catch (enrollErr) {
          setSubmitError('איש הקשר מוזג אך ההרשמה לשכר לימוד נכשלה: ' + extractError(enrollErr));
          setSaving(false);
          onSaved({ contactId: duplicateContact._id });
          return;
        }
      }

      onSaved({ contactId: duplicateContact._id });
      onClose();
    } catch (err) {
      console.error(err);
      setSubmitError(extractError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // If there are duplicates and one is selected, merge instead of create
    if (selectedDuplicate && duplicates.length > 0) {
      const dup = duplicates.find(d => d._id === selectedDuplicate);
      if (dup) {
        await mergeToDuplicate(dup);
        return;
      }
    }

    // When opened from the tuition screen (locked institution), a billing family
    // is mandatory — derived from the student's last name unless overridden.
    if (institutionContext?.lock && formData.type === 'student' && !effectiveFamilyName) {
      setSubmitError('שם משפחה נדרש');
      return;
    }

    setSaving(true);
    setSubmitError(null);
    try {
      const payload = buildPayload();
      const res = await api.post('/contacts', payload);
      const newContact = res.data;

      // Optional tuition enrollment — only when a student has an institution +
      // billing family. Without them we just created a student-typed contact.
      if (formData.type === 'student' && selectedInstitutionId && effectiveFamilyName) {
        try {
          const famId = await enrollTuition(newContact._id);
          onSaved({ contactId: newContact._id, familyId: famId });
          onClose();
          return;
        } catch (enrollErr) {
          // Contact is saved; only the enrollment failed.
          setSubmitError('איש הקשר נוצר אך ההרשמה לשכר לימוד נכשלה: ' + extractError(enrollErr));
          setSaving(false);
          onSaved({ contactId: newContact._id });
          return;
        }
      }

      onSaved({ contactId: newContact._id });
      onClose();
    } catch (err) {
      console.error(err);
      setSubmitError(extractError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 overflow-y-auto" dir="rtl">
      <div className="bg-white dark:bg-[#1E1E1E] rounded-2xl shadow-xl w-full md:max-w-2xl mx-4 my-8 max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 md:px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
          <h3 className="text-lg font-bold text-[#1C1B1F] dark:text-white">{formData.type === 'student' ? '🎓 תלמיד חדש' : '➕ איש קשר חדש'}</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
            <X size={20} />
          </button>
        </div>

        {/* גוף נגלל */}
        <div className="flex-1 overflow-y-auto px-4 md:px-6 py-5">

        {/* Duplicate warning */}
        {duplicates.length > 0 && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4 mb-5">
            <div className="flex gap-3">
              <div className="text-yellow-600 dark:text-yellow-400 mt-0.5">⚠️</div>
              <div className="flex-1">
                <p className="font-semibold text-yellow-800 dark:text-yellow-300 mb-2">נמצא איש קשר דומה</p>
                <div className="space-y-2">
                  {duplicates.map(dup => (
                    <label key={dup._id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-yellow-100 dark:hover:bg-yellow-800/30 cursor-pointer">
                      <input
                        type="radio"
                        name="mergeDuplicate"
                        value={dup._id}
                        checked={selectedDuplicate === dup._id}
                        onChange={() => setSelectedDuplicate(dup._id)}
                        onClick={() => { if (selectedDuplicate === dup._id) setSelectedDuplicate(null); }}
                        className="accent-yellow-600"
                      />
                      <div className="text-sm">
                        <p className="font-medium text-gray-800 dark:text-gray-200">{getDisplayName(dup)}</p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">{dup.email && `${dup.email} · `}{dup.phone}</p>
                      </div>
                    </label>
                  ))}
                </div>
                {selectedDuplicate ? (
                  <div className="flex items-center justify-between gap-2 mt-2">
                    <p className="text-xs text-yellow-700 dark:text-yellow-300">
                      ✓ הפרטים החדשים יתמזגו לאיש הקשר הקיים
                    </p>
                    <button type="button" onClick={() => setSelectedDuplicate(null)}
                      className="text-xs font-bold text-yellow-800 dark:text-yellow-200 underline hover:no-underline whitespace-nowrap">
                      בטל בחירה — צור איש קשר חדש
                    </button>
                  </div>
                ) : (
                  <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-2">
                    לא לבחור כלום = ייווצר איש קשר חדש נפרד.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {submitError && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-3 mb-4 text-sm text-red-800 dark:text-red-300">
            <strong>שמירה נכשלה:</strong> {submitError}
          </div>
        )}

        <form id="add-contact-form" onSubmit={handleSubmit} className="space-y-5">
          {/* Profile photo */}
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 flex items-center justify-center flex-shrink-0">
              {formData.photo
                ? <img src={formData.photo} alt="" className="w-full h-full object-cover" />
                : <Camera size={22} className="text-gray-400" />}
            </div>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => photoInputRef.current?.click()} disabled={uploadingPhoto}
                className="px-3 py-1.5 text-xs font-medium border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
                {uploadingPhoto ? 'מעלה…' : formData.photo ? 'החלף תמונה' : 'העלה תמונה'}
              </button>
              {formData.photo && (
                <button type="button" onClick={() => setFormData(f => ({ ...f, photo: '' }))}
                  className="px-2 py-1.5 text-xs text-gray-500 hover:text-red-500">הסר</button>
              )}
              <input ref={photoInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoChange} />
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-500 mb-3">פרטים בסיסיים</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">שם פרטי *</label>
                <input required type="text" placeholder="ישראל" value={formData.firstName} onChange={e => set('firstName', e.target.value)} className={FIELD_CLASS} />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">שם משפחה *</label>
                <input required type="text" placeholder="ישראלי" value={formData.lastName} onChange={e => set('lastName', e.target.value)} className={FIELD_CLASS} />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">טלפון</label>
                <input type="tel" placeholder="050-0000000" value={formData.phone} onChange={e => set('phone', e.target.value)} className={FIELD_CLASS} />
              </div>
              <div className="col-span-1 sm:col-span-2">
                <label className="block text-xs text-gray-500 mb-1">אימייל</label>
                <EmailListEditor rows={emailRows} onChange={handleEmailRows} />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">תעודת זהות</label>
                <input type="text" placeholder="000000000" value={formData.nationalId} onChange={e => set('nationalId', e.target.value)} className={FIELD_CLASS} />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">עיר</label>
                <input type="text" placeholder="ירושלים" value={formData.city} onChange={e => set('city', e.target.value)} className={FIELD_CLASS} />
              </div>
              {/* יומולדת לכל סוגי אנשי הקשר — לתלמיד הוא כבר מופיע בבלוק שכר הלימוד */}
              {formData.type !== 'student' && (
                <div>
                  <label className="block text-xs text-gray-500 mb-1">תאריך לידה</label>
                  <HebrewDateInput value={formData.birthDate} onChange={v => set('birthDate', v)} className={FIELD_CLASS} />
                </div>
              )}
              {formData.type !== 'student' && (
                <div>
                  <label className="block text-xs text-gray-500 mb-1">מגדר</label>
                  <select value={formData.gender} onChange={e => set('gender', e.target.value)} className={FIELD_CLASS}>
                    <option value="">לא צוין</option>
                    <option value="male">זכר</option>
                    <option value="female">נקבה</option>
                    <option value="other">אחר</option>
                  </select>
                </div>
              )}
              {/* the type picker is hidden when launched from the tuition
                  screen — it's a student form, the type is fixed */}
              {!institutionContext?.lock && (
                <div>
                  <label className="block text-xs text-gray-500 mb-1">סוג</label>
                  <select value={formData.type} onChange={e => set('type', e.target.value)} className={FIELD_CLASS}>
                    <option value="member">כללי / חבר</option>
                    <option value="donor">תורם</option>
                    <option value="supported">נתמך</option>
                    <option value="supplier">ספק</option>
                    <option value="parent">הורה</option>
                    <option value="student">תלמיד</option>
                    <option value="alumni">בוגר</option>
                    <option value="employee">עובד</option>
                    <option value="avrech">אברך</option>
                    <option value="yeshiva_alumni">בוגר הישיבה</option>
                    <option value="daycare">מעון יום</option>
                    <option value="community">קהילה</option>
                    <option value="volunteer">מתנדב</option>
                    {(features.trainingGroups || formData.type === 'trainee' || formData.type === 'coach') && (
                      <>
                        <option value="trainee">מתאמן</option>
                        <option value="coach">מאמן</option>
                      </>
                    )}
                    <option value="other">אחר</option>
                  </select>
                </div>
              )}
              <div>
                <label className="block text-xs text-gray-500 mb-1">סטטוס</label>
                {(formData.type === 'student' || formData.type === 'alumni') ? (
                  // a student's status is student/alumni — not the CRM
                  // donor statuses (פעיל/ליד/VIP)
                  <select value={formData.type} onChange={e => set('type', e.target.value)} className={FIELD_CLASS}>
                    <option value="student">תלמיד/ה</option>
                    <option value="alumni">בוגר/ת</option>
                  </select>
                ) : (
                  <select value={formData.status} onChange={e => set('status', e.target.value)} className={FIELD_CLASS}>
                    <option value="active">פעיל</option>
                    <option value="lead">ליד</option>
                    <option value="vip">VIP</option>
                    <option value="inactive">לא פעיל</option>
                    <option value="blocked">חסום</option>
                  </select>
                )}
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-gray-500 mb-1">תגיות (הפרדה בפסיקים)</label>
                <input type="text" placeholder="תגית1, תגית2" value={formData.tags} onChange={e => set('tags', e.target.value)} className={FIELD_CLASS} />
              </div>

              {/* Tuition enrollment — only for students */}
              {formData.type === 'student' && (
                <div className="col-span-2 border border-indigo-200 dark:border-indigo-800 bg-indigo-50/40 dark:bg-indigo-900/10 rounded-xl p-3 space-y-3">
                  <div className="text-xs font-bold text-indigo-700 dark:text-indigo-300">🎓 שיוך לשכר לימוד {institutionContext?.lock ? '' : '(אופציונלי)'}</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">מוסד</label>
                      {institutionContext?.lock ? (
                        <input disabled className={FIELD_CLASS + ' opacity-70'} value={lockedInstName} />
                      ) : (
                        <select className={FIELD_CLASS} value={selectedInstitutionId} onChange={e => { setSelectedInstitutionId(e.target.value); setUnitId(''); }}>
                          <option value="">— ללא שיוך —</option>
                          {institutions.map(i => <option key={i._id} value={i._id}>{i.name}</option>)}
                        </select>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">תאריך לידה</label>
                      <HebrewDateInput value={formData.birthDate} onChange={v => set('birthDate', v)} className={FIELD_CLASS} />
                    </div>
                  </div>

                  {selectedInstitutionId && (
                    <>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">{classLabel}</label>
                        {classOptions.length ? (
                          <select className={FIELD_CLASS} value={unitId} onChange={e => setUnitId(e.target.value)}>
                            <option value="">— ללא שיוך —</option>
                            {classOptions.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                          </select>
                        ) : <p className="text-xs text-amber-600">אין כיתות עדיין — הוסף כיתה/שכבה במסך שכר לימוד</p>}
                        <p className="text-[10px] text-gray-400 mt-1">התלמיד וההורים ייכנסו אוטומטית לקבוצות של הכיתה, השכבה והמוסד.</p>
                      </div>

                      {/* Billing family defaults to the student's last name */}
                      {showFamilyNameField ? (
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">שם המשפחה לחיוב</label>
                          <input className={FIELD_CLASS} value={customFamilyName} onChange={e => setCustomFamilyName(e.target.value)} placeholder={effectiveFamilyName || 'משפחת לוי'} autoFocus />
                        </div>
                      ) : (
                        <p className="text-[11px] text-gray-500">
                          החיוב יירשם על {effectiveFamilyName || 'שם המשפחה של התלמיד'}.{' '}
                          <button type="button" onClick={() => setShowFamilyNameField(true)} className="text-indigo-600 font-semibold hover:underline">שם משפחה שונה לחיוב?</button>
                        </p>
                      )}
                    </>
                  )}
                  {!institutionContext?.lock && (
                    <p className="text-[10px] text-gray-400">בחירת מוסד תיצור הרשמה לחיוב שכר לימוד. אחרת ייווצר רק איש קשר מסוג תלמיד.</p>
                  )}
                </div>
              )}

              {/* Parents — a single block: saved on the student's card AND (for
                  enrolled students) as the family's payers. No double entry. */}
              {((formData.type as string) === 'student' || (formData.type as string) === 'alumni') && (
                <div className="col-span-2 border border-gray-200 dark:border-gray-700 rounded-xl p-3">
                  <div className="text-xs font-bold text-gray-600 dark:text-gray-300 mb-2">👨‍👩‍👦 הורים</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <div className="text-[11px] font-bold text-gray-500">אב</div>
                      <input className={FIELD_CLASS} placeholder="שם האב" value={(formData as any).studentParents.fatherName} onChange={e => setSP('fatherName', e.target.value)} />
                      <input className={FIELD_CLASS} placeholder="טלפון האב" dir="ltr" value={(formData as any).studentParents.fatherPhone} onChange={e => setSP('fatherPhone', e.target.value)} />
                      <input className={FIELD_CLASS} placeholder="מייל האב" dir="ltr" value={(formData as any).studentParents.fatherEmail} onChange={e => setSP('fatherEmail', e.target.value)} />
                      <input className={FIELD_CLASS} placeholder="ת״ז האב" dir="ltr" value={(formData as any).studentParents.fatherId} onChange={e => setSP('fatherId', e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <div className="text-[11px] font-bold text-gray-500">אם</div>
                      <input className={FIELD_CLASS} placeholder="שם האם" value={(formData as any).studentParents.motherName} onChange={e => setSP('motherName', e.target.value)} />
                      <input className={FIELD_CLASS} placeholder="טלפון האם" dir="ltr" value={(formData as any).studentParents.motherPhone} onChange={e => setSP('motherPhone', e.target.value)} />
                      <input className={FIELD_CLASS} placeholder="מייל האם" dir="ltr" value={(formData as any).studentParents.motherEmail} onChange={e => setSP('motherEmail', e.target.value)} />
                      <input className={FIELD_CLASS} placeholder="ת״ז האם" dir="ltr" value={(formData as any).studentParents.motherId} onChange={e => setSP('motherId', e.target.value)} />
                    </div>
                  </div>
                  {formData.type === 'student' && selectedInstitutionId && (
                    <div className="mt-3 pt-2 border-t border-gray-100 dark:border-gray-700">
                      <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300 cursor-pointer">
                        <input type="checkbox" checked={splitPayment} onChange={e => setSplitPayment(e.target.checked)} className="w-4 h-4 accent-indigo-600 rounded" />
                        חלוקת תשלום בין ההורים (גרושים)
                      </label>
                      {splitPayment && (
                        <div className="flex items-center gap-3 mt-2 text-xs">
                          <span>אב</span>
                          <input type="number" className="w-16 px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700" value={fatherPercent} onChange={e => setFatherPercent(e.target.value)} dir="ltr" />%
                          <span>אם</span>
                          <input type="number" className="w-16 px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700" value={motherPercent} onChange={e => setMotherPercent(e.target.value)} dir="ltr" />%
                          <span className={`font-semibold ${(Number(fatherPercent) || 0) + (Number(motherPercent) || 0) === 100 ? 'text-emerald-600' : 'text-amber-600'}`}>
                            = {(Number(fatherPercent) || 0) + (Number(motherPercent) || 0)}%
                          </span>
                        </div>
                      )}
                      {!splitPayment && <p className="text-[10px] text-gray-400 mt-1">ללא חלוקה — ההורה הראשון שמולא הוא המשלם. כל הורה נשמר כאיש קשר ב-CRM.</p>}
                    </div>
                  )}
                </div>
              )}
              {groups.length > 0 && formData.type === 'student' && !showExtraGroups ? (
                <div className="col-span-2">
                  <button type="button" onClick={() => setShowExtraGroups(true)} className="text-xs text-gray-400 hover:text-indigo-600 font-semibold">
                    + שיוך לקבוצה נוספת (לא חובה)
                  </button>
                </div>
              ) : groups.length > 0 && (
                <div className="col-span-2" ref={groupPickerRef}>
                  <label className="block text-xs text-gray-500 mb-1">{formData.type === 'student' ? 'שיוך לקבוצה נוספת' : 'שיוך לקבוצות'}</label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowGroupPicker(v => !v)}
                      className={`${FIELD_CLASS} flex items-center justify-between gap-2 text-right cursor-pointer min-h-[42px]`}
                    >
                      <div className="flex flex-wrap gap-1 flex-1">
                        {selectedGroupIds.length === 0 ? (
                          <span className="text-gray-400">בחר קבוצות...</span>
                        ) : (
                          selectedGroupIds.map(id => {
                            const g = groups.find(gr => gr._id === id);
                            if (!g) return null;
                            return (
                              <span
                                key={id}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-white"
                                style={{ backgroundColor: g.color || '#6366f1' }}
                              >
                                {g.name}
                                <X
                                  size={12}
                                  className="cursor-pointer hover:opacity-70"
                                  onClick={(e) => { e.stopPropagation(); toggleGroup(id); }}
                                />
                              </span>
                            );
                          })
                        )}
                      </div>
                      <ChevronDown size={16} className="text-gray-400 flex-shrink-0" />
                    </button>
                    {showGroupPicker && (
                      <div className="absolute z-20 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-64 overflow-hidden flex flex-col">
                        <div className="p-2 border-b border-gray-100 dark:border-gray-700">
                          <div className="relative">
                            <Search size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                              type="text"
                              autoFocus
                              placeholder="חיפוש קבוצה..."
                              value={groupSearch}
                              onChange={e => setGroupSearch(e.target.value)}
                              className="w-full pr-8 pl-2 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                            />
                          </div>
                        </div>
                        <div className="overflow-y-auto">
                          {groups
                            .filter(g => g.name.toLowerCase().includes(groupSearch.toLowerCase()))
                            .map(g => {
                              const checked = selectedGroupIds.includes(g._id);
                              return (
                                <label
                                  key={g._id}
                                  className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer text-sm"
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleGroup(g._id)}
                                    className="accent-blue-600"
                                  />
                                  <span
                                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: g.color || '#6366f1' }}
                                  />
                                  <span className="flex-1 text-gray-800 dark:text-gray-200">{g.name}</span>
                                  <span className="text-xs text-gray-400">{g.count ?? g.contactIds?.length ?? 0}</span>
                                </label>
                              );
                            })}
                          {groups.filter(g => g.name.toLowerCase().includes(groupSearch.toLowerCase())).length === 0 && (
                            <p className="px-3 py-3 text-sm text-gray-400 text-center">לא נמצאו קבוצות</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <button
            type="button"
            onClick={() => setShowAdvanced(v => !v)}
            className="flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700"
          >
            {showAdvanced ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            פרטים נוספים
          </button>

          {showAdvanced && (
            <div className="space-y-4 pt-2 border-t border-gray-100 dark:border-gray-700">
              <div>
                <p className="text-xs font-semibold text-gray-500 mb-2">📞 יצירת קשר</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">טלפון בית</label>
                    <input type="tel" placeholder="03-0000000" value={formData.phoneHome} onChange={e => set('phoneHome', e.target.value)} className={FIELD_CLASS} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">וואטסאפ (אם שונה)</label>
                    <input type="tel" placeholder="050-0000000" value={formData.whatsapp} onChange={e => set('whatsapp', e.target.value)} className={FIELD_CLASS} />
                  </div>
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-500 mb-2">🏠 כתובת</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">רחוב</label>
                    <input type="text" placeholder="שם הרחוב" value={formData.street} onChange={e => set('street', e.target.value)} className={FIELD_CLASS} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">מספר בית</label>
                    <input type="text" placeholder="מספר" value={formData.streetNumber} onChange={e => set('streetNumber', e.target.value)} className={FIELD_CLASS} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">מיקוד</label>
                    <input type="text" placeholder="1234567" value={formData.zipCode} onChange={e => set('zipCode', e.target.value)} className={FIELD_CLASS} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">ת.ד.</label>
                    <input type="text" placeholder="תיבת דואר" value={formData.poBox} onChange={e => set('poBox', e.target.value)} className={FIELD_CLASS} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">מדינה</label>
                    <input type="text" value={formData.country} onChange={e => set('country', e.target.value)} className={FIELD_CLASS} />
                  </div>
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-500 mb-2">🕍 קהילה</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">בית כנסת / קהילה</label>
                    <input type="text" placeholder="שם הקהילה" value={formData.synagogue} onChange={e => set('synagogue', e.target.value)} className={FIELD_CLASS} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">שם הרב</label>
                    <input type="text" placeholder="הרב..." value={formData.rabbi} onChange={e => set('rabbi', e.target.value)} className={FIELD_CLASS} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">עדה</label>
                    <input type="text" placeholder="ספרדי / אשכנזי / תימני..." value={formData.ethnicity} onChange={e => set('ethnicity', e.target.value)} className={FIELD_CLASS} />
                  </div>
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-500 mb-2">👨‍👩‍👧‍👦 משפחה</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">מצב משפחתי</label>
                    <select value={formData.maritalStatus} onChange={e => set('maritalStatus', e.target.value)} className={FIELD_CLASS}>
                      <option value="">לא צוין</option>
                      <option value="single">רווק/ה</option>
                      <option value="married">נשוי/אה</option>
                      <option value="divorced">גרוש/ה</option>
                      <option value="widowed">אלמן/ה</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">שם בן/בת זוג</label>
                    <input type="text" placeholder="שם" value={formData.spouseName} onChange={e => set('spouseName', e.target.value)} className={FIELD_CLASS} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">מספר ילדים</label>
                    <input type="number" min="0" placeholder="0" value={formData.numberOfChildren} onChange={e => set('numberOfChildren', e.target.value)} className={FIELD_CLASS} />
                  </div>
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-500 mb-2">📊 CRM</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">מקור</label>
                    <select value={formData.source} onChange={e => set('source', e.target.value)} className={FIELD_CLASS}>
                      <option value="">לא צוין</option>
                      <option value="campaign">קמפיין</option>
                      <option value="event">אירוע</option>
                      <option value="referral">הפניה</option>
                      <option value="manual">ידני</option>
                      <option value="import">יבוא</option>
                      <option value="form">טופס</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">הופנה על ידי</label>
                    <input type="text" placeholder="שם המפנה" value={formData.referredBy} onChange={e => set('referredBy', e.target.value)} className={FIELD_CLASS} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">העדפת תקשורת</label>
                    <select value={formData.communicationPref} onChange={e => set('communicationPref', e.target.value)} className={FIELD_CLASS}>
                      <option value="">לא צוין</option>
                      <option value="whatsapp">וואטסאפ</option>
                      <option value="email">אימייל</option>
                      <option value="phone">טלפון</option>
                      <option value="sms">SMS</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Documents */}
          <div className="space-y-2 pt-2 border-t border-gray-100 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-gray-500">מסמכים מצורפים</p>
              <button
                type="button"
                onClick={() => docInputRef.current?.click()}
                disabled={uploadingDoc}
                className="text-xs font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1 disabled:opacity-50"
              >
                <Paperclip size={14} /> {uploadingDoc ? 'מעלה…' : 'הוסף מסמך'}
              </button>
              <input ref={docInputRef} type="file" className="hidden" onChange={handleDocChange} accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" />
            </div>
            {pendingDocs.length > 0 && (
              <ul className="space-y-1">
                {pendingDocs.map((d, i) => (
                  <li key={i} className="flex items-center justify-between text-sm bg-gray-50 dark:bg-gray-800 rounded-lg px-3 py-1.5">
                    <span className="truncate flex-1">📎 {d.name}</span>
                    <button type="button" onClick={() => setPendingDocs(arr => arr.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-500 flex-shrink-0">
                      <Trash2 size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Custom Fields Section with Add Field Button */}
          <div className="space-y-4 pt-2 border-t border-gray-100 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-gray-500 mb-2">שדות מותאמים</p>
              <button
                type="button"
                onClick={() => setShowAddFieldForm(!showAddFieldForm)}
                className="text-xs font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1"
              >
                <Plus size={14} /> הוסף שדה
              </button>
            </div>

            {/* Add Field Form — shared creator (כולל סוג תאריך) */}
            {showAddFieldForm && (
              <AddCustomFieldInline
                existingFields={customFields}
                onCreated={(_f, all) => { setCustomFields(all as any); setShowAddFieldForm(false); }}
                onCancel={() => setShowAddFieldForm(false)}
              />
            )}

            {/* Display Existing Custom Fields — by group */}
            {visibleFieldsByGroup.map(({ group, fields }) => (
              <div key={group.id} className="space-y-2">
                {(visibleFieldsByGroup.length > 1 || group.id !== 'default') && (
                  <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-300 mt-3 mb-1">{group.label}</h4>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {fields.map((field) => (
                    <div key={field.id} className={field.type === 'checkbox' ? 'flex items-center gap-3 pt-5' : ''}>
                      {field.type !== 'checkbox' && (
                        <label className="block text-xs text-gray-500 mb-1">
                          {field.label}{field.required && <span className="text-red-500 mr-1">*</span>}
                        </label>
                      )}
                      {field.type === 'text' && (
                        <input
                          type="text"
                          placeholder={field.placeholder || field.label}
                          value={customFieldValues[field.id] || ''}
                          onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [field.id]: e.target.value }))}
                          className={FIELD_CLASS}
                        />
                      )}
                      {field.type === 'number' && (
                        <input
                          type="number"
                          placeholder={field.placeholder || field.label}
                          value={customFieldValues[field.id] || ''}
                          onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [field.id]: e.target.value }))}
                          className={FIELD_CLASS}
                        />
                      )}
                      {field.type === 'date' && (
                        <input
                          type="date"
                          value={customFieldValues[field.id] || ''}
                          onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [field.id]: e.target.value }))}
                          className={FIELD_CLASS}
                        />
                      )}
                      {field.type === 'select' && (
                        <select
                          value={customFieldValues[field.id] || ''}
                          onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [field.id]: e.target.value }))}
                          className={FIELD_CLASS}
                        >
                          <option value="">-- בחר --</option>
                          {(field.options || []).map((opt: string) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      )}
                      {field.type === 'checkbox' && (
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={customFieldValues[field.id] === 'true'}
                            onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [field.id]: e.target.checked ? 'true' : '' }))}
                            className="w-4 h-4 accent-blue-600 rounded"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{field.label}</span>
                        </label>
                      )}
                      {field.helpText && (
                        <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">{field.helpText}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

        </form>
        </div>

        {/* כותרת תחתונה קבועה */}
        <div className="flex gap-2 justify-end px-4 md:px-6 py-4 border-t border-gray-100 dark:border-gray-800 flex-shrink-0">
          <Button type="button" variant="secondary" onClick={onClose}>ביטול</Button>
          <Button type="submit" form="add-contact-form" variant="primary" disabled={saving}>{saving ? 'שומר...' : 'שמור'}</Button>
        </div>
      </div>
    </div>
  );
};

export default AddContactForm;
